/**
 * Inviting somebody, and following what happened to the invitation.
 *
 * Shared between the account page (an inviter's own invitations) and the admin
 * page (every invitation on the deployment), because "where has this person
 * got to?" reads the same in both: invited → signed in → downloaded.
 *
 * The outcome of sending is always said in full. An invitation authorises the
 * address whether or not the email went, so a failed email is not a failed
 * invitation — and the inviter has to be told which one happened, with the
 * link to pass on by hand when it is the email that failed.
 */

import { useCallback, useState, type FormEvent, type ReactNode } from "react";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import type { Invitation, InvitationSent } from "@xtract-bot/embellisher-schema";
import { api, errorMessage } from "../api";
import { useAsync } from "../useAsync";
import { ConfirmModal, CopyField, EmptyState, ErrorAlert, InlineSpinner, SkeletonRows, TimeAgo, formatAbsolute, useConfirm } from "./ui";

/* ── One invitation's progress ────────────────────────────────────────────── */

export function InvitationProgress({ invitation }: { invitation: Invitation }): ReactNode {
  if (invitation.status === "withdrawn") {
    return (
      <span className="small emb-muted">
        <Badge bg="secondary">withdrawn</Badge> <TimeAgo iso={invitation.withdrawnAt} />
      </span>
    );
  }
  return (
    <div className="small d-flex flex-column gap-1">
      <div>
        {invitation.downloadedAt ? (
          <Badge bg="success">downloaded</Badge>
        ) : invitation.status === "accepted" ? (
          <Badge bg="primary">signed in</Badge>
        ) : (
          <Badge bg="warning" text="dark">
            waiting
          </Badge>
        )}
      </div>
      <div className="emb-muted">
        {invitation.lastSentAt ? (
          <>
            emailed <TimeAgo iso={invitation.lastSentAt} />
            {invitation.sendCount > 1 ? ` · ${invitation.sendCount} times` : ""}
          </>
        ) : (
          "not emailed"
        )}
      </div>
      {invitation.lastSendError ? (
        <div className="text-danger" title={invitation.lastSendError}>
          last send failed
        </div>
      ) : null}
    </div>
  );
}

/** Turns a send result into the sentence the inviter needs to read. Also used by Admin → Access, whose additions are invitations too. */
export function SendOutcome({ result, onClose }: { result: InvitationSent; onClose: () => void }): ReactNode {
  return (
    <Alert variant={result.emailed ? "success" : "warning"} onClose={onClose} dismissible className="small">
      {result.emailed ? (
        <p className="mb-2">
          <strong>{result.invitation.email}</strong> is invited, and the email with the install steps is on its way.
          {result.invitation.addedToAccessList ? " Their address was added to the access list." : " Their address was already allowed in."}
        </p>
      ) : (
        <>
          <p className="mb-2">
            <strong>{result.invitation.email}</strong> is invited
            {result.invitation.addedToAccessList ? " and on the access list" : ""} — but the email did not go.
          </p>
          <p className="mb-2">{result.emailProblem}</p>
          <p className="mb-2">Send them this link yourself. It asks them to sign in with that address, then offers the download and the steps:</p>
          <CopyField value={result.installUrl} />
        </>
      )}
      {result.warnings.map((warning) => (
        <p key={warning} className="mb-0 mt-2 emb-muted">
          {warning}
        </p>
      ))}
    </Alert>
  );
}

/* ── The table ────────────────────────────────────────────────────────────── */

export function InvitationsTable({
  invitations,
  showInviter = false,
  canResend,
  onResend,
  onWithdraw,
}: {
  invitations: Invitation[];
  showInviter?: boolean;
  /** Resending goes out in the inviter's name, so only they may; the admin table never offers it. */
  canResend: boolean;
  onResend?: (invitation: Invitation) => void;
  onWithdraw?: (invitation: Invitation) => void;
}): ReactNode {
  return (
    <Table responsive hover className="align-middle mb-0">
      <thead>
        <tr>
          <th>Invited</th>
          {showInviter ? <th>By</th> : null}
          <th>Progress</th>
          <th className="text-end">Sent</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {invitations.map((invitation) => (
          <tr key={invitation.inviteId} className={invitation.status === "withdrawn" ? "opacity-50" : ""}>
            <td>
              <span className="emb-mono">{invitation.email}</span>
              {invitation.note ? (
                <div className="emb-muted small text-truncate" style={{ maxWidth: "20rem" }} title={invitation.note}>
                  “{invitation.note}”
                </div>
              ) : null}
            </td>
            {showInviter ? (
              <td className="small">
                <div>{invitation.invitedBy.username}</div>
                <div className="emb-muted">{invitation.invitedBy.email}</div>
              </td>
            ) : null}
            <td>
              <InvitationProgress invitation={invitation} />
            </td>
            <td className="text-end small" title={formatAbsolute(invitation.createdAt)}>
              <TimeAgo iso={invitation.createdAt} />
            </td>
            <td className="text-end">
              {invitation.status === "pending" ? (
                <div className="d-flex gap-3 justify-content-end">
                  {canResend && onResend ? (
                    <Button size="sm" variant="link" className="p-0" onClick={() => onResend(invitation)}>
                      Resend
                    </Button>
                  ) : null}
                  {onWithdraw ? (
                    <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => onWithdraw(invitation)}>
                      Withdraw
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** The withdraw confirmation, worded for what it actually does to the access list. */
export function WithdrawInvitationModal({
  confirm,
  onWithdrawn,
}: {
  confirm: ReturnType<typeof useConfirm<Invitation>>;
  onWithdrawn: () => Promise<void> | void;
}): ReactNode {
  const invitation = confirm.subject;
  return (
    <ConfirmModal
      show={invitation !== null}
      title="Withdraw this invitation?"
      confirmLabel="Withdraw"
      variant="danger"
      busy={confirm.busy}
      error={confirm.error}
      onCancel={confirm.cancel}
      onConfirm={() =>
        void confirm.run(async (subject) => {
          await api.withdrawInvitation(subject.inviteId);
          await onWithdrawn();
        })
      }
    >
      <p className="mb-0">
        The link in the email they were sent stops getting <span className="emb-mono">{invitation?.email}</span> in.
        {invitation?.addedToAccessList
          ? " The invitation put their address on the access list, so it comes off again — unless somebody else has invited them too."
          : " Their address was already allowed by another rule, which is left as it is."}
      </p>
    </ConfirmModal>
  );
}

/* ── The account page's card ──────────────────────────────────────────────── */

/**
 * Hidden entirely for an account that may not invite and never has: a card
 * explaining a power you do not have is noise on somebody's account page.
 */
export function InvitationsCard(): ReactNode {
  const page = useAsync(() => api.invitations(), []);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<InvitationSent | null>(null);
  const withdraw = useConfirm<Invitation>();

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      if (!email.trim()) return;
      setBusy(true);
      setError(null);
      setOutcome(null);
      try {
        setOutcome(await api.invite(email.trim(), note.trim() || undefined));
        setEmail("");
        setNote("");
        await page.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      } finally {
        setBusy(false);
      }
    },
    [email, note, page],
  );

  const resend = useCallback(
    async (invitation: Invitation) => {
      setError(null);
      setOutcome(null);
      try {
        setOutcome(await api.resendInvitation(invitation.inviteId));
        await page.reload();
      } catch (cause) {
        setError(errorMessage(cause));
      }
    },
    [page],
  );

  if (page.loading) return null;
  if (page.error) return <ErrorAlert error={page.error} onRetry={() => void page.reload()} />;
  const data = page.data;
  if (!data || (!data.canInvite && data.invitations.length === 0)) return null;

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Invite people</span>
        {page.reloading ? <InlineSpinner label="Refreshing" /> : null}
      </Card.Header>
      <Card.Body>
        {data.canInvite ? (
          <Form onSubmit={(event) => void submit(event)} className="mb-3">
            <Row className="g-2 align-items-end">
              <Col md={5}>
                <Form.Group controlId="emb-invite-email">
                  <Form.Label className="small emb-muted">Their email address</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="someone@example.com"
                    autoComplete="off"
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group controlId="emb-invite-note">
                  <Form.Label className="small emb-muted">A line from you (optional)</Form.Label>
                  <Form.Control value={note} onChange={(event) => setNote(event.target.value)} placeholder="This is the tool I mentioned" maxLength={1000} />
                </Form.Group>
              </Col>
              <Col md={2} className="d-grid">
                <Button type="submit" variant="primary" disabled={busy || email.trim().length === 0}>
                  {busy ? "Inviting…" : "Invite"}
                </Button>
              </Col>
            </Row>
            <Form.Text className="emb-muted d-block mt-2">
              Inviting adds the address to this deployment's access list and emails them step-by-step instructions: sign in
              with that address, download the extension, load it into Chrome, link it and connect their assistant. The
              download is only offered once they have signed in. Replies to the email come to you.
              {data.emailConfigured ? null : (
                <strong className="d-block mt-1">
                  This deployment cannot send email yet, so you will be given the link to send them yourself.
                </strong>
              )}
            </Form.Text>
          </Form>
        ) : (
          <p className="emb-muted small">You can no longer invite people here; the invitations you sent are below.</p>
        )}

        {error ? (
          <Alert variant="danger" className="small" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        ) : null}
        {outcome ? <SendOutcome result={outcome} onClose={() => setOutcome(null)} /> : null}
      </Card.Body>
      {data.invitations.length === 0 ? (
        <Card.Body className="pt-0">
          <EmptyState title="Nobody invited yet">Each invitation you send shows here, with whether they have signed in and downloaded the extension.</EmptyState>
        </Card.Body>
      ) : (
        <InvitationsTable
          invitations={data.invitations}
          canResend={data.canInvite}
          onResend={(invitation) => void resend(invitation)}
          onWithdraw={(invitation) => withdraw.ask(invitation)}
        />
      )}
      <WithdrawInvitationModal confirm={withdraw} onWithdrawn={() => page.reload()} />
    </Card>
  );
}

/** For the admin page, which lists every invitation and loads its own. */
export function AllInvitations({ canWithdraw }: { canWithdraw: boolean }): ReactNode {
  const all = useAsync(() => api.adminInvitations(), []);
  const withdraw = useConfirm<Invitation>();
  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>Every invitation</span>
        {all.reloading ? <InlineSpinner label="Refreshing" /> : null}
      </Card.Header>
      {all.loading ? (
        <Card.Body>
          <SkeletonRows rows={4} />
        </Card.Body>
      ) : all.error ? (
        <Card.Body>
          <ErrorAlert error={all.error} onRetry={() => void all.reload()} />
        </Card.Body>
      ) : !all.data || all.data.length === 0 ? (
        <Card.Body>
          <EmptyState title="No invitations yet">An invitation appears here the moment somebody sends one.</EmptyState>
        </Card.Body>
      ) : (
        <InvitationsTable
          invitations={all.data}
          showInviter
          canResend={false}
          {...(canWithdraw ? { onWithdraw: (invitation: Invitation) => withdraw.ask(invitation) } : {})}
        />
      )}
      <WithdrawInvitationModal confirm={withdraw} onWithdrawn={() => all.reload()} />
    </Card>
  );
}
